// PaymentPage.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import BASE_URL from "../../config/config";
import CardToCardPayment from "./CardToCardPayment";
import ZarinpalPayment from "./ZarinpalPayment";
import "../../css/PaymentPage.css";
import { convertToJalali } from "../../utils/dateConverter";

const PaymentPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [csrfToken, setCsrfToken] = useState("");
  const [courseData, setCourseData] = useState(null);
  const [orderState, setOrderState] = useState("");
  const [orderMessage, setOrderMessage] = useState("");
  const [isFreeCourse, setIsFreeCourse] = useState(false);
  const [discount, setDiscount] = useState(0);
  
  // انتخاب روش پرداخت: 'card-to-card' یا 'online'
  const [paymentMethod, setPaymentMethod] = useState(null);

  const jalaliTodayFull = convertToJalali(new Date());
  const jalaliTodayDateOnly = jalaliTodayFull.fullDate.split(" ")[0];

  // دریافت CSRF
  useEffect(() => {
    fetch(`${BASE_URL}/csrf-token`, {
      credentials: "include",
    })
      .then((r) => r.json())
      .then((d) => setCsrfToken(d.csrfToken))
      .catch(() => setOrderMessage("مشکل در دریافت توکن CSRF"));
  }, []);

  // چک وضعیت سفارش
  useEffect(() => {
    if (!csrfToken) return;

    const fetchCourseStatus = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        setOrderMessage("ابتدا وارد حساب کاربری خود شوید.");
        return;
      }

      setOrderMessage("در حال بارگذاری اطلاعات...");
      try {
        const resp = await fetch(`${BASE_URL}/user/courses/${id}`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "CSRF-Token": csrfToken,
          },
          credentials: "include",
        });

        const resData = await resp.json();
        const status = resData.enrollmentStatus;

        if (!resp.ok)
          throw new Error(resData.message || "خطا در دریافت وضعیت ثبت‌نام");

        if (status === "completed") {
          setOrderMessage("✅ شما قبلاً این دوره را ثبت‌نام کرده‌اید.");
          setCourseData(resData.data);
          setOrderState("success");
          setTimeout(() => navigate(`/courses/${id}`), 2500);
        } else if (status === "pending") {
          setOrderMessage(
            "🔄 سفارش شما هنوز تکمیل نشده است. لطفاً منتظر تأیید بمانید."
          );
          setCourseData(resData.data);
          setOrderState("pending");
          setDiscount(resData.data.discount || 0);
        } else if (status === "available") {
          setOrderMessage(
            "🟢 اطلاعات دوره بارگذاری شد، می‌توانید ثبت‌نام کنید."
          );
          setCourseData(resData.data);
          setOrderState("available");
          setDiscount(resData.data.discount || 0);
          if (resData.data.price == 0) setIsFreeCourse(true);
        } else {
          setOrderMessage("❌ وضعیت نامعتبر از سرور دریافت شد.");
        }
      } catch (err) {
        setOrderMessage("خطا در ارتباط با سرور: " + err.message);
      }
    };

    fetchCourseStatus();
  }, [csrfToken, id, navigate]);

  // ثبت خودکار دوره رایگان
  useEffect(() => {
    if (isFreeCourse && courseData) {
      (async () => {
        try {
          setOrderMessage("در حال ثبت سفارش رایگان...");
          const postOrder = await fetch(`${BASE_URL}/orders`, {
            method: "POST",
            headers: {
              "CSRF-Token": csrfToken,
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify({
              course_id: id,
              description: JSON.stringify({ note: "ثبت سفارش رایگان" }),
              price: 0,
              service_type: "programming",
              paymentDate: jalaliTodayDateOnly,
              payment_method: "free",
            }),
          });
          const postData = await postOrder.json();
          if (!postOrder.ok)
            throw new Error(postData.message || "خطا در ثبت سفارش رایگان");
          setOrderMessage(
            "دوره شما ثبت گردید و به زودی به صفحه دوره منتقل می‌شوید."
          );
          setTimeout(() => navigate(`/courses/${id}`), 2000);
        } catch (err) {
          setOrderMessage("خطا در ثبت دوره رایگان: " + err.message);
        }
      })();
    }
  }, [isFreeCourse, courseData, csrfToken, id, navigate, jalaliTodayDateOnly]);

  const calculateFinalPrice = (price, discountPercent) => {
    const originalPrice = parseFloat(price) || 0;
    const discount = parseFloat(discountPercent) || 0;
    return originalPrice - (originalPrice * discount) / 100;
  };

  // ========== RENDER ==========
  return (
    <div className="main-content-wrapper">
      <div className="payment-container">
        {(orderState === "pending" || orderState === "available") &&
          courseData &&
          courseData.title && (
            <>
              {/* هدر دوره */}
              <div className="payment-form">
                <div className="form-header">
                  پرداخت دوره: {courseData.title}
                  <span className="card-icon">💳</span>
                </div>

                {/* قیمت */}
                <div className="price-box">
                  {discount > 0 ? (
                    <>
                      <div className="price-with-discount">
                        <span className="original-price strikethrough">
                          {courseData.price.toLocaleString()} تومان
                        </span>
                        <span className="discount-badge">{discount}% تخفیف</span>
                        <span className="final-price">
                          {calculateFinalPrice(
                            courseData.price,
                            discount
                          ).toLocaleString()}{" "}
                          تومان
                        </span>
                      </div>
                      <span>هزینه نهایی دوره</span>
                    </>
                  ) : (
                    <>
                      <span>{courseData.price.toLocaleString()} تومان</span>
                      <span>هزینه دوره</span>
                    </>
                  )}
                </div>

                {/* انتخاب روش پرداخت */}
                {!isFreeCourse && !paymentMethod && (
                  <div className="payment-method-selector">
                    <h3>لطفاً روش پرداخت خود را انتخاب کنید:</h3>
                    <div className="payment-methods">
                      <button
                        className="payment-method-btn card-to-card-btn"
                        onClick={() => setPaymentMethod("card-to-card")}
                      >
                        <span className="method-icon">💳</span>
                        <span className="method-title">کارت به کارت</span>
                        <span className="method-desc">
                          واریز دستی به شماره کارت
                        </span>
                      </button>

                      <button
                        className="payment-method-btn online-btn"
                        onClick={() => setPaymentMethod("online")}
                      >
                        <span className="method-icon">🌐</span>
                        <span className="method-title">پرداخت آنلاین</span>
                        <span className="method-desc">
                          درگاه پرداخت زرین‌پال
                        </span>
                      </button>
                    </div>
                  </div>
                )}

                {/* نمایش کامپوننت مربوط به روش انتخابی */}
                {paymentMethod === "card-to-card" && (
                  <CardToCardPayment
                    courseData={courseData}
                    discount={discount}
                    csrfToken={csrfToken}
                    jalaliTodayDateOnly={jalaliTodayDateOnly}
                    onBack={() => setPaymentMethod(null)}
                  />
                )}

                {paymentMethod === "online" && (
                  <ZarinpalPayment
                    courseData={courseData}
                    discount={discount}
                    csrfToken={csrfToken}
                    onBack={() => setPaymentMethod(null)}
                  />
                )}
              </div>
            </>
          )}

        {/* نمایش پیام وضعیت */}
        {orderMessage && (
          <div
            className={`alert ${
              orderMessage.includes("خطا") || orderMessage.includes("❌")
                ? "alert-danger"
                : orderMessage.includes("بارگذاری") ||
                  orderMessage.includes("🔄")
                ? "alert-info"
                : "alert-success"
            }`}
            style={{
              padding: "15px",
              borderRadius: "8px",
              marginTop: "20px",
              textAlign: "center",
              fontSize: "16px",
            }}
          >
            {orderMessage}
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentPage;
